// Eventos estándar del píxel de Meta que dispara la app desde el navegador, además del
// `PageView` que ya emite `index.html`. El `Purchase` NO va por acá: lo manda el
// servidor de pagos por la Conversions API en el primer cobro
// (`atlas-payment-server/src/lib/meta.ts`), y así sigue.
//
// Tres eventos, tres momentos (ADR-2026-10-12-eventos-de-meta-del-embudo):
//   StartTrial       → la empresa recién creada arranca en el plan gratis (createCompany)
//   InitiateCheckout → se abre el checkout con un plan elegido (Checkout / EnterpriseCheckout)
//   AddPaymentInfo   → se envía la tarjeta en el form de Rebill, o se está por ir a la
//                      página de pago de Stripe (lo último que la app ve del exterior)
//
// Best-effort TOTAL: sin `fbq` (ad blocker, script que no cargó, tests en node) no pasa
// nada, y jamás lanza — un alta o un cobro no se rompen por medición. Cada envío lleva
// un `eventID` propio para que, si mañana el backend manda el mismo evento por la
// Conversions API, Meta deduplique por ese id en vez de contarlo dos veces.

import { esPrimeraVezEnEstaCarga } from './embudoAltaEmpresa';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

export type EventoDeMeta = 'StartTrial' | 'InitiateCheckout' | 'AddPaymentInfo';

/** Parámetros estándar de Meta para un evento sobre un plan. */
export interface ParametrosDePlan {
  content_name: string;
  content_ids: string[];
  content_type: 'product';
  num_items: 1;
  value?: number;
  currency?: string;
}

/**
 * Lo que Meta espera de un evento sobre un plan. `value`/`currency` van juntos y sólo
 * cuando hay un precio real (> 0) y una moneda: un plan a medida cuyo precio todavía
 * no se resolvió viaja sin valor antes que con un "0" que Meta tomaría como cierto.
 * El `content_name` lleva el mismo "Plan {plan}" que el `Purchase` del servidor, para
 * que el embudo se lea con un solo nombre de punta a punta.
 */
export function parametrosDePlan(
  plan: string,
  precio?: number | null,
  moneda?: string | null,
): ParametrosDePlan {
  const out: ParametrosDePlan = {
    content_name: `Plan ${plan}`,
    content_ids: [plan],
    content_type: 'product',
    num_items: 1,
  };
  const cod = (moneda ?? '').trim().toUpperCase();
  if (typeof precio === 'number' && Number.isFinite(precio) && precio > 0 && cod) {
    out.value = Math.round(precio * 100) / 100;
    out.currency = cod;
  }
  return out;
}

function nuevoEventId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* cae al fallback */
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Dispara un evento estándar del píxel. Devuelve `true` sólo si de verdad se llamó a
 * `fbq`.
 *
 * `claveUnica`: si viene, el evento sale UNA vez por carga de página para esa clave
 * (mismo mecanismo que el embudo del alta por link: el árbol de rutas se remonta al
 * resolver la sesión y un `useRef` no alcanza). Sin clave, sale cada vez.
 */
export function trackMeta(
  evento: EventoDeMeta,
  params?: ParametrosDePlan,
  claveUnica?: string,
): boolean {
  try {
    if (typeof window === 'undefined' || typeof window.fbq !== 'function') return false;
    if (claveUnica && !esPrimeraVezEnEstaCarga(`meta:${evento}:${claveUnica}`)) return false;
    window.fbq('track', evento, params ?? {}, { eventID: nuevoEventId() });
    return true;
  } catch {
    return false;
  }
}
